import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "./api";
import { setLocale, getLocale, normalizeLocale } from "../i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null = checking session; object = signed in; false = anonymous
  const [user, setUser] = useState(null);

  const applyServerLocale = useCallback((u) => {
    if (!u) return;
    const serverLoc = u.locale ? normalizeLocale(u.locale) : null;
    const localLoc = getLocale();
    if (serverLoc && serverLoc !== localLoc) {
      // Server is the source of truth when the user has explicitly set one.
      setLocale(serverLoc);
    } else if (!serverLoc) {
      // First sign-in for this user — push the detected/local locale to server.
      api.patch("/auth/me/locale", { locale: localLoc }).catch(() => { /* ignore */ });
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      applyServerLocale(res.data);
      return res.data;
    } catch {
      setUser(false);
      return null;
    }
  }, [applyServerLocale]);

  useEffect(() => {
    fetchMe();
    if (typeof window === "undefined") return;
    const onMfaRequired = () => { fetchMe(); };
    window.addEventListener("mfa-enrollment-required", onMfaRequired);
    window.addEventListener("email-verification-required", fetchMe);
    return () => {
      window.removeEventListener("mfa-enrollment-required", onMfaRequired);
      window.removeEventListener("email-verification-required", fetchMe);
    };
  }, [fetchMe]);

  const login = async ({ email, password }) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data && res.data.mfa_required) {
      return res.data;
    }
    setUser(res.data);
    applyServerLocale(res.data);
    return res.data;
  };

  const verify2fa = async ({ code }) => {
    const res = await api.post("/auth/2fa/verify", { code });
    setUser(res.data);
    applyServerLocale(res.data);
    return res.data;
  };

  const setup2fa = async () => (await api.post("/auth/2fa/setup")).data;
  const enable2fa = async ({ code }) => (await api.post("/auth/2fa/enable", { code })).data;
  const disable2fa = async ({ password }) => (await api.post("/auth/2fa/disable", { password })).data;
  const regenerateBackupCodes = async ({ password }) =>
    (await api.post("/auth/2fa/backup-codes/regenerate", { password })).data;
  const get2faStatus = async () => (await api.get("/auth/2fa/status")).data;

  const register = async ({ email, password, name, terms_accepted }) => {
    // Send the currently-detected locale so the server can persist it on creation.
    // Iter76 — `terms_accepted` is a required boolean, gated by the UI checkbox.
    const res = await api.post("/auth/register", {
      email, password, name, locale: getLocale(),
      terms_accepted: terms_accepted === true,
    });
    setUser(res.data);
    applyServerLocale(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(false);
  };

  const forgotPassword = async ({ email }) => {
    const res = await api.post("/auth/forgot-password", { email });
    return res.data;
  };

  const resetPassword = async ({ token, password }) => {
    const res = await api.post("/auth/reset-password", { token, password });
    return res.data;
  };

  const resendVerification = async () => {
    const res = await api.post("/auth/email/verify/resend");
    return res.data;
  };

  const updateProfile = async ({ name }) => {
    const res = await api.patch("/auth/me", { name });
    setUser((prev) => (prev ? { ...prev, name: res.data.name } : prev));
    return res.data;
  };

  const deleteAccount = async ({ currentPassword, totpCode }) => {
    // axios exige `data` pour porter un corps sur un DELETE.
    await api.delete("/auth/me", {
      data: { current_password: currentPassword, totp_code: totpCode || undefined },
    });
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        resendVerification,
        updateProfile,
        deleteAccount,
        refresh: fetchMe,
        verify2fa,
        setup2fa,
        enable2fa,
        disable2fa,
        regenerateBackupCodes,
        get2faStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an <AuthProvider>");
  return ctx;
}

export { formatApiErrorDetail };
